import { auth, firestore } from '../firebase';
import { doc, updateDoc } from 'firebase/firestore';
import { buildApiUrl } from '../config/env';

async function withAuthHeaders() {
  const currentUser = auth.currentUser;
  if (!currentUser) throw new Error('User must be authenticated');
  const token = await currentUser.getIdToken();
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

export async function listModelsAdmin() {
  const headers = await withAuthHeaders();
  const res = await fetch(buildApiUrl('/admin/models'), { headers });
  if (!res.ok) throw new Error((await res.json()).error || 'Failed to fetch models');
  return res.json();
}

export async function saveModelAdmin(model) {
  const headers = await withAuthHeaders();
  const res = await fetch(buildApiUrl('/admin/models'), {
    method: 'POST',
    headers,
    body: JSON.stringify(model),
  });
  if (!res.ok) throw new Error((await res.json()).error || 'Failed to save model');
  return res.json();
}

export async function bulkUpdateModelsAdmin(models) {
  const headers = await withAuthHeaders();
  const res = await fetch(buildApiUrl('/admin/models/bulk'), {
    method: 'POST',
    headers,
    body: JSON.stringify({ models }),
  });
  if (!res.ok) throw new Error((await res.json()).error || 'Failed to import models');
  return res.json();
}

export async function deleteModelAdmin(id) {
  const headers = await withAuthHeaders();
  const res = await fetch(buildApiUrl(`/admin/models/${id}`), {
    method: 'DELETE',
    headers,
  });
  if (!res.ok) throw new Error((await res.json()).error || 'Failed to delete model');
  return res.json();
}

export async function fetchAdminDashboardUsage({ startDate, endDate, topUsers, topModels, userId, metric } = {}) {
  const headers = await withAuthHeaders();
  const params = new URLSearchParams();
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  if (topUsers != null) params.set('topUsers', String(topUsers));
  if (topModels != null) params.set('topModels', String(topModels));
  if (userId) params.set('userId', userId);
  if (metric) params.set('metric', metric);

  const queryString = params.toString();
  const endpoint = queryString
    ? buildApiUrl(`/admin/dashboard?${queryString}`)
    : buildApiUrl('/admin/dashboard');
  const res = await fetch(endpoint, { headers });
  if (!res.ok) throw new Error((await res.json()).error || 'Failed to fetch admin dashboard data');
  return res.json();
}

const filenameFromContentDisposition = (header) => {
  if (!header) return null;

  const encodedMatch = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (encodedMatch?.[1]) {
    try {
      return decodeURIComponent(encodedMatch[1]);
    } catch (_) {
      return encodedMatch[1];
    }
  }

  const plainMatch = header.match(/filename="?([^"]+)"?/i);
  return plainMatch?.[1] || null;
};

async function readErrorMessage(res, fallback) {
  try {
    const data = await res.clone().json();
    return data.error || fallback;
  } catch (_) {
    try {
      const text = await res.text();
      return text || fallback;
    } catch (_error) {
      return fallback;
    }
  }
}

export async function fetchAdminDashboardUsageExport({
  startDate,
  endDate,
  userId,
  models,
  format = 'csv',
} = {}) {
  const headers = await withAuthHeaders();
  const params = new URLSearchParams();
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  if (userId) params.set('userId', userId);
  if (format) params.set('format', format);
  (models || []).forEach((model) => {
    if (model) params.append('model', model);
  });

  const queryString = params.toString();
  const endpoint = queryString
    ? buildApiUrl(`/admin/dashboard/export?${queryString}`)
    : buildApiUrl('/admin/dashboard/export');
  const res = await fetch(endpoint, { headers });

  if (!res.ok) {
    throw new Error(await readErrorMessage(res, 'Failed to export admin dashboard data'));
  }

  return {
    blob: await res.blob(),
    filename: filenameFromContentDisposition(res.headers.get('Content-Disposition')),
  };
}

// Update only Work availability; existing Firebase rules require the admin claim.
export async function setModelWorkEnabled(id, enabled) {
  if (!auth.currentUser || (await auth.currentUser.getIdTokenResult()).claims.admin !== true) {
    throw new Error('Admin privileges required');
  }
  await updateDoc(doc(firestore, 'models', id), { workEnabled: enabled === true });
}
